/**
 * Cart Utilities - Centrale cart management
 * Vervangt duplicated code in product-form.js, kc-card-product-js.liquid, etc.
 */
(function() {
  'use strict';

  // Voorkom dubbele initialisatie
  if (window.CartUtils) return;

  // ============================================
  // CART CACHE & STATE
  // ============================================
  var cartCache = null;
  var cartCacheTime = 0;
  var CACHE_TTL = 2000; // 2 seconden cache
  var pendingCartFetch = null;

  // ============================================
  // CORE FUNCTIONS
  // ============================================

  /**
   * Fetch JSON met error handling
   */
  function fetchJSON(url, options) {
    return fetch(url, options).then(function(response) {
      if (!response.ok) {
        return Promise.reject(response);
      }
      return response.json();
    });
  }

  /**
   * Haal cart op (met caching)
   * @param {boolean} forceRefresh - Forceer verse data
   * @returns {Promise<Object>} Cart object
   */
  function getCart(forceRefresh) {
    var now = Date.now();
    
    // Return cached als geldig
    if (!forceRefresh && cartCache && (now - cartCacheTime) < CACHE_TTL) {
      return Promise.resolve(cartCache);
    }
    
    // Voorkom parallelle requests
    if (pendingCartFetch) {
      return pendingCartFetch;
    }
    
    pendingCartFetch = fetchJSON('/cart.js')
      .then(function(cart) {
        cartCache = cart;
        cartCacheTime = Date.now();
        pendingCartFetch = null;
        return cart;
      })
      .catch(function(err) {
        pendingCartFetch = null;
        throw err;
      });
    
    return pendingCartFetch;
  }

  /**
   * Invalideer cart cache (na add/remove/update)
   */
  function invalidateCache() {
    cartCache = null;
    cartCacheTime = 0;
  }

  /**
   * Haal quantity in cart voor specifieke variant
   */
  function getCartQty(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var vid = String(variantId);
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === vid) {
        return cart.items[i].quantity || 0;
      }
    }
    return 0;
  }

  /**
   * Haal totale quantity voor een product (alle varianten)
   */
  function getProductTotalInCart(cart, productId) {
    if (!cart || !Array.isArray(cart.items) || !productId) return 0;
    var pid = String(productId);
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].product_id) === pid) {
        total += cart.items[i].quantity || 0;
      }
    }
    return total;
  }

  // ============================================
  // UI UPDATE FUNCTIONS
  // ============================================

  // Alle mogelijke cart count selectors
  var CART_COUNT_SELECTORS = [
    '[data-cart-count]',
    '.cart-count-bubble span',
    '.cart-count-bubble .text',
    '.cart-count-bubble .count',
    '.cart-count',
    '.header__cart-count',
    '.site-header__cart-count',
    '.cart-link__count',
    '.cart-icon__count',
    '.js-cart-count',
    '#cart-icon-bubble span',
    '.header__icon--cart span',
    '.header__icon--cart .cart-count',
    '.header__icon--cart [data-count]',
    '[data-cart-item-count]',
    '.site-header__cart .cart__count'
  ];

  /**
   * Update alle cart badges in de UI
   */
  function updateCartBadges() {
    return getCart(true).then(function(cart) {
      var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
      
      CART_COUNT_SELECTORS.forEach(function(selector) {
        document.querySelectorAll(selector).forEach(function(el) {
          if (el.textContent !== String(count)) {
            el.textContent = count;
          }
          if (el.classList) {
            el.classList.remove('hidden');
            if (count > 0) {
              el.classList.add('has-items');
            } else {
              el.classList.remove('has-items');
            }
          }
        });
      });
      
      // Dispatch event voor andere componenten
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cart, count: count } 
      }));
      
      return cart;
    }).catch(function(err) {
      console.warn('[CartUtils] Could not update cart badges:', err);
    });
  }

  // ============================================
  // TOAST NOTIFICATIONS
  // ============================================

  var activeToast = null;
  var toastTimeout = null;

  /**
   * Toon toast notificatie
   * @param {string} message - Bericht
   * @param {string} type - 'success', 'error', 'info'
   * @param {number} duration - Duur in ms (default 2000)
   */
  function toast(message, type, duration) {
    type = type || 'info';
    duration = duration || 2000;
    
    // Verwijder bestaande toast
    if (activeToast) {
      activeToast.remove();
      clearTimeout(toastTimeout);
    }
    
    var t = document.createElement('div');
    t.id = 'kc-toast';
    t.className = 'kc-toast kc-toast--' + type;
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    t.textContent = message || '';
    
    // Styling
    var bgColor = type === 'error' ? '#dc2626' : 
                  type === 'success' ? '#16a34a' : '#111';
    
    t.style.cssText = 'position:fixed;z-index:9999;left:50%;bottom:20px;' +
      'transform:translateX(-50%);background:' + bgColor + ';color:#fff;' +
      'padding:12px 18px;border-radius:10px;font-size:14px;font-weight:500;' +
      'box-shadow:0 6px 20px rgba(0,0,0,.25);opacity:0;transition:opacity .2s ease;' +
      'max-width:90vw;text-align:center;';
    
    document.body.appendChild(t);
    activeToast = t;
    
    // Fade in
    requestAnimationFrame(function() {
      t.style.opacity = '1';
    });
    
    // Auto hide
    toastTimeout = setTimeout(function() {
      t.style.opacity = '0';
      setTimeout(function() {
        if (t.parentNode) t.remove();
        if (activeToast === t) activeToast = null;
      }, 200);
    }, duration);
  }

  // ============================================
  // BUTTON LOADING STATES
  // ============================================

  /**
   * Maak een spinner element
   */
  function createSpinner() {
    var spinner = document.createElement('span');
    spinner.className = 'btn-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    // Inline styles als backup voor CSS
    spinner.style.cssText = 'position:absolute;top:50%;left:50%;width:22px;height:22px;margin:-11px 0 0 -11px;border:3px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;background:transparent;box-sizing:border-box;pointer-events:none;z-index:100;animation:cart-utils-spin 0.6s linear infinite;';
    return spinner;
  }

  /**
   * Maak een checkmark element
   */
  function createCheckmark() {
    var checkmark = document.createElement('span');
    checkmark.className = 'btn-checkmark';
    checkmark.setAttribute('aria-hidden', 'true');
    
    // Gebruik setProperty voor echte !important - forceer ALLES
    checkmark.style.setProperty('position', 'absolute', 'important');
    checkmark.style.setProperty('top', '50%', 'important');
    checkmark.style.setProperty('left', '50%', 'important');
    checkmark.style.setProperty('width', '22px', 'important');
    checkmark.style.setProperty('height', '22px', 'important');
    checkmark.style.setProperty('margin', '-11px 0 0 -11px', 'important');
    checkmark.style.setProperty('pointer-events', 'none', 'important');
    checkmark.style.setProperty('z-index', '99999', 'important');
    checkmark.style.setProperty('display', 'block', 'important');
    checkmark.style.setProperty('opacity', '1', 'important');
    checkmark.style.setProperty('visibility', 'visible', 'important');
    checkmark.style.setProperty('transform', 'translate(-50%, -50%) scale(1)', 'important');
    
    // SVG checkmark icoon
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('width', '22');
    svg.setAttribute('height', '22');
    svg.style.setProperty('width', '22px', 'important');
    svg.style.setProperty('height', '22px', 'important');
    svg.style.setProperty('display', 'block', 'important');
    svg.style.setProperty('opacity', '1', 'important');
    svg.style.setProperty('visibility', 'visible', 'important');
    
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20 6L9 17l-5-5');
    path.setAttribute('stroke', '#ffffff');
    path.setAttribute('stroke-width', '3');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('fill', 'none');
    path.style.setProperty('stroke', '#ffffff', 'important');
    path.style.setProperty('opacity', '1', 'important');
    
    svg.appendChild(path);
    checkmark.appendChild(svg);
    
    return checkmark;
  }

  /**
   * Verwijder spinner/checkmark elementen van button
   * (Alleen voor dynamisch toegevoegde elementen - HTML elementen blijven staan)
   */
  function removeIndicators(button) {
    if (!button) return;
    // Verwijder alleen dynamisch toegevoegde spinners (niet .kc-btn-loading die al in HTML staat)
    var dynamicSpinner = button.querySelector('.btn-spinner');
    var dynamicCheckmark = button.querySelector('.btn-checkmark');
    if (dynamicSpinner) dynamicSpinner.remove();
    if (dynamicCheckmark) dynamicCheckmark.remove();
  }

  /**
   * Check of button een KC card button is (heeft eigen spinner/checkmark)
   * Product page buttons (add-cart-button) zijn GEEN KC card buttons
   */
  function isKcCardButton(button) {
    if (!button) return false;
    
    // Product page buttons zijn NOOIT KC card buttons
    if (button.classList.contains('add-cart-button')) return false;
    if (button.id && button.id.startsWith('Product-Submit-Button')) return false;
    
    // KC card buttons hebben deze kenmerken
    return button.classList.contains('kc-btn') || 
           button.closest('.kc-card') !== null ||
           button.hasAttribute('data-card-add-button') ||
           button.hasAttribute('data-kc-pen');
  }

  /**
   * Start loading state op button
   * Gebruikt .is-loading class (net zoals product cards)
   * .kc-btn-loading element staat al in HTML
   */
  function startLoading(button) {
    if (!button) return;
    
    // Verwijder eventuele oude indicators
    removeIndicators(button);
    
    button.classList.add('is-loading');
    button.classList.remove('is-success', 'is-error');
    button.setAttribute('aria-busy', 'true');
    
    // Geen dynamische elementen toevoegen - HTML heeft al .kc-btn-loading
  }

  /**
   * Stop loading en toon success
   * Gebruikt classes + inline styles voor groene achtergrond (net zoals product cards)
   * .kc-btn-check element staat al in HTML
   */
  function showSuccess(button, duration) {
    if (!button) return;
    duration = duration || 1500;
    
    // Verwijder spinner eerst
    removeIndicators(button);
    
    // Check of dit een KC card button is
    var isKcBtn = isKcCardButton(button);
    
    if (!isKcBtn) {
      // Voor product page buttons: forceer groene achtergrond via inline styles
      var originalBg = button.style.background || '';
      var originalBgColor = button.style.backgroundColor || '';
      
      button.style.setProperty('background', '#16a34a', 'important');
      button.style.setProperty('background-color', '#16a34a', 'important');
    }
    
    // Voeg success state toe - CSS zorgt voor de rest
    button.classList.remove('is-loading');
    button.classList.add('is-success');
    button.removeAttribute('aria-busy');
    
    // Reset na timeout
    setTimeout(function() {
      button.classList.remove('is-success');
      
      if (!isKcBtn) {
        button.style.background = originalBg;
        button.style.backgroundColor = originalBgColor;
      }
      
      removeIndicators(button);
    }, duration);
  }

  /**
   * Stop loading en toon error (shake animatie)
   */
  function showError(button) {
    if (!button) return;
    
    // Verwijder spinner
    removeIndicators(button);
    
    button.classList.remove('is-loading');
    button.classList.add('is-error', 'add-to-cart-button-shake');
    button.removeAttribute('aria-busy');
    
    setTimeout(function() {
      button.classList.remove('is-error', 'add-to-cart-button-shake');
    }, 500);
  }

  /**
   * Stop loading zonder feedback
   */
  function stopLoading(button) {
    if (!button) return;
    removeIndicators(button);
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  }

  // ============================================
  // ERROR TOAST (met cart icon shake)
  // ============================================

  /**
   * Toon error toast en shake cart icon
   */
  function showCartErrorToast(message, triggerButton) {
    toast(message, 'error', 2500);
    
    // Shake cart icon in header
    var cartIcons = document.querySelectorAll('.header__icon--cart, [data-cart-icon], .cart-icon');
    cartIcons.forEach(function(icon) {
      icon.classList.add('shake-animation');
      setTimeout(function() {
        icon.classList.remove('shake-animation');
      }, 500);
    });
    
    // Shake trigger button indien aanwezig
    if (triggerButton) {
      showError(triggerButton);
    }
  }

  // ============================================
  // ADD TO CART
  // ============================================

  /**
   * Voeg item toe aan cart
   * @param {Object} options
   * @param {string|number} options.variantId - Variant ID
   * @param {number} options.quantity - Quantity (default 1)
   * @param {HTMLElement} options.button - Button element voor feedback
   * @param {Object} options.properties - Line item properties
   * @returns {Promise<Object>} Response van cart/add.js
   */
  function addToCart(options) {
    var variantId = options.variantId;
    var quantity = options.quantity || 1;
    var button = options.button;
    var properties = options.properties || {};
    
    // Start loading
    if (button) {
      startLoading(button);
    }
    
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    
    // Properties toevoegen
    Object.keys(properties).forEach(function(key) {
      formData.append('properties[' + key + ']', properties[key]);
    });
    
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    })
    .then(function(response) {
      return response.json().then(function(data) {
        return { response: response, data: data };
      });
    })
    .then(function(result) {
      var data = result.data;
      
      // Check voor errors
      if (data.status) {
        var description = data.description || '';
        var isPartialAdd = description.toLowerCase().indexOf('was added') !== -1 ||
                           description.toLowerCase().indexOf('were added') !== -1 ||
                           description.indexOf('toegevoegd vanwege') !== -1;
        
        if (isPartialAdd) {
          // Partial add: items ZIJN toegevoegd, update cart
          invalidateCache();
          updateCartBadges();
          
          if (button) {
            showSuccess(button);
          }
          
          // Return partial add info
          return {
            success: true,
            partial: true,
            message: description,
            data: data
          };
        }
        
        // Full error
        throw new Error(description || 'CART_ERROR');
      }
      
      // Success
      invalidateCache();
      updateCartBadges();
      
      if (button) {
        showSuccess(button);
      }
      
      return {
        success: true,
        partial: false,
        data: data
      };
    })
    .catch(function(err) {
      if (button) {
        showError(button);
      }
      
      throw err;
    });
  }

  // ============================================
  // TRANSLATIONS HELPER
  // ============================================

  /**
   * Haal vertaling op uit window.cartStrings
   * @param {string} key - Vertaling key
   * @param {Object} replacements - Placeholder replacements
   * @returns {string} Vertaalde tekst
   */
  function t(key, replacements) {
    var strings = window.cartStrings || {};
    var text = strings[key] || key;
    
    if (replacements) {
      Object.keys(replacements).forEach(function(placeholder) {
        text = text.replace('[' + placeholder + ']', replacements[placeholder]);
        text = text.replace('{{ ' + placeholder + ' }}', replacements[placeholder]);
      });
    }
    
    return text;
  }

  // ============================================
  // EXPORT
  // ============================================

  window.CartUtils = {
    // Cart data
    getCart: getCart,
    invalidateCache: invalidateCache,
    getCartQty: getCartQty,
    getProductTotalInCart: getProductTotalInCart,
    
    // UI updates
    updateCartBadges: updateCartBadges,
    
    // Notifications
    toast: toast,
    showCartErrorToast: showCartErrorToast,
    
    // Button states
    startLoading: startLoading,
    showSuccess: showSuccess,
    showError: showError,
    stopLoading: stopLoading,
    
    // Cart operations
    addToCart: addToCart,
    
    // Translations
    t: t,
    
    // Fetch helper
    fetchJSON: fetchJSON
  };

  // Legacy compatibility - expose als window functions
  window.updateCartBadges = updateCartBadges;
  window.showCartErrorToast = showCartErrorToast;
  window.startAddToCartLoading = startLoading;
  window.showAddToCartSuccess = showSuccess;
  window.showAddToCartFeedback = showSuccess; // Alias

  // Debug
  if (window.location.search.indexOf('debug=cart') !== -1) {
    console.log('[CartUtils] Initialized', window.CartUtils);
  }

})();
